import {
  ComparatorContext,
  IProduct,
  Review,
  useCurrentStepIndex,
  useField,
  usePage,
  useWizardService,
} from '@kizi/comparator-core';
import { Button, Div, Image } from '@kizi/core-components';
import { IconV2 as Icon } from '@kizi/shared-components';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import {
  Attributes,
  Desktop,
  isMobile,
  Mobile,
  Price,
  RatingReviews,
} from '../../';

interface PriceGroup {
  total: number;
  totalTax?: number;
  discountIncluded: number;
  taxRate?: number;
}

interface ProductPrice {
  breakdown: any;
  year: PriceGroup;
  month: PriceGroup;
}

interface ProductProps {
  attributes: Attributes;
  bigProductFeature: {
    hideIfZero: boolean;
    label: string;
    value: number;
    variant: string;
  };
  discount: number;
  features: any;
  id: string;
  image: string;
  initialValue: number;
  name: string;
  oneOffCostsAmount: number;
  price: ProductPrice;
  priceLabel: string;
  productDescription: string;
  providerDescription: string;
  purchasableProduct: boolean;
  review?: Review;
  sustainabilityCommerceScore: string;
  sustainabilityInvestmentScore: string;
  sustainabilityScore: string;
  total: number;
}

export interface CompareSelectProps {
  products: any[];
  priceMode: 'year' | 'month';
  setProducts: React.Dispatch<React.SetStateAction<any[]>>;
  isIProducts?: boolean;
}

export const CompareSelect: React.FC<CompareSelectProps> = (props) => {
  const { state, setState } = usePage();
  const context = useContext(ComparatorContext);
  const isCar = context.name === 'car';
  const comparisonProductsFiled = useField('overview.comparisonProducts');
  const comparisonProducts = JSON.parse(
    comparisonProductsFiled?.value
      ? (comparisonProductsFiled?.value as string)
      : '[]',
  );

  useEffect(() => {
    setState &&
      setState({
        ...state,
        onCompareChange: handleCompareChange(props.products),
      });
  }, [JSON.stringify(props.products)]);

  const fieldValueChange = (name: string, value: string) => {
    value &&
      name &&
      name.length > 0 &&
      wizard?.send({
        type: 'CHANGE',
        target: { name, value },
      });
  };

  const handleCompareChange =
    (products) => (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
      const productIndex = products.findIndex((product) => product.id === id);
      const selectedProducts = products.filter(function (product) {
        return product.compare === true;
      });

      const allowedLength = isMobile() ? 1 : 2;

      if (productIndex >= 0) {
        if (
          selectedProducts.length > allowedLength &&
          !products[productIndex].compare
        ) {
          return;
        }
        products[productIndex].compare =
          e != null ? e.target.checked : !products[productIndex].compare;
      }

      const saveCompareFieldValues = products.filter(function (product) {
        return product.compare === true;
      });

      fieldValueChange(
        'comparisonProducts',
        JSON.stringify(saveCompareFieldValues),
      );

      props.setProducts(products);
    };

  const comparableProducts = useMemo(
    () => props.products.filter((product) => product.compare === true),
    [JSON.stringify(props.products)],
  );

  const shouldShowCompare = useMemo((): boolean => {
    const compareProducts = props.products.filter(
      (product) => product.compare === true,
    );

    return compareProducts.length > 0;
  }, [JSON.stringify(props.products)]);

  const wizard = useWizardService();
  const currentIndex = useCurrentStepIndex();

  const onCompare = useCallback(() => {
    if (currentIndex !== undefined)
      wizard?.send({ type: 'GOTO', index: currentIndex + 1 });
  }, [wizard]);

  useEffect(() => {
    fieldValueChange(
      'comparisonProducts',
      JSON.stringify(
        props.products.filter(function (product) {
          return product.compare === true;
        }),
      ),
    );
  }, [props.products]);

  const onCompareClose = (id: string) => {
    props.setProducts((previousVisibleProducts) => {
      return previousVisibleProducts.map((product) => {
        if (product.id === id) product.compare = false;
        return product;
      });
    });
  };

  const renderImage = (image: string): any => {
    return (
      <>
        {image ? (
          <Div pt={10} pl={10} pb={10}>
            <Image
              alt={'logo-${comparableProduct.name}'}
              src={image}
              width={80}
            />
          </Div>
        ) : (
          <Div height={40} />
        )}
      </>
    );
  };

  const renderReview = (review?: Review): any => {
    return (
      <>
        {review ? (
          <RatingReviews display="block" data={review} pl={10} />
        ) : (
          <Div height={30} />
        )}
      </>
    );
  };

  //When using in other comparators then car modifications may be required here
  const getProductProps = (data, selectedPriceMode): ProductProps => {
    let obj: ProductProps = {
      attributes: {
        annual_account: {
          digital: '',
          mail: '',
        },
        edit_amount: {
          app: '',
          online: '',
          phone: '',
        },
        extraInfo: [],
        moveWindow: {
          maxDays: 0,
          minDays: 0,
        },
        meter_readings: {
          app: '',
          online: '',
          phone: '',
        },
        noReflectionPeriod: false,
        payment_method: {
          acceptgiro: '',
          automatic: '',
          iDeal: '',
        },
        postcoderoosregeling: '',
        prosAndCons: {
          pros: {
            title1: '',
            title2: '',
            text1: '',
            text2: '',
          },
          cons: {
            title1: '',
            title2: '',
            text1: '',
            text2: '',
          },
        },
        reviews: {
          bad_indicator: [],
          customer_score: '',
          expert_quote: '',
          good_indicator: [],
          helpdesk_score: '',
          rs_customer_service_total: '',
          rs_durability_total: '',
          rs_online_service_total: '',
          rs_payment_total: '',
          rs_price_understanding_total: '',
          rs_transfer_process_total: '',
          total_score: '',
        },
        sustainabilityScore: '',
        switchCountWeek: 0,
        switchWindow: {
          maxDays: 0,
          minDays: 0,
        },
        usage: {
          app: '',
          online: '',
          phone: '',
        },
      },
      bigProductFeature: {
        hideIfZero: false,
        label: '',
        value: 0,
        variant: 'priceMedium',
      },
      discount: 0,
      features: {},
      id: '',
      image: '',
      initialValue: 0,
      name: '',
      oneOffCostsAmount: 0,
      priceLabel: 'per ' + (selectedPriceMode === 'month' ? 'maand' : 'yaar'),
      productDescription: '',
      providerDescription: '',
      purchasableProduct: true,
      price: {
        breakdown: {},
        month: { discountIncluded: 0, total: 0 },
        year: { discountIncluded: 0, total: 0 },
      },
      review: undefined,
      sustainabilityCommerceScore: '',
      sustainabilityInvestmentScore: '',
      sustainabilityScore: '',
      total: 0,
    };

    const { availability, features, id, image, price, provider, review } = data;

    const {
      attributes = {},
      description: providerDescription,
      name,
    } = provider;

    const {
      sustainabilityScore = '',
      sustainabilityCommerceScore = '',
      sustainabilityInvestmentScore = '',
    } = attributes;

    const discount: number = price[props.priceMode].discountIncluded;

    obj = {
      ...obj,
      attributes,
      discount,
      features,
      id,
      image: image || provider.logo,
      name,
      price,
      providerDescription,
      purchasableProduct: availability?.acceptance?.accepted,
      review,
      sustainabilityCommerceScore,
      sustainabilityInvestmentScore,
      sustainabilityScore,
      total: price[props.priceMode].total,
    };

    return obj;
  };

  const getIProductProps = (product: IProduct, priceMode: 'month' | 'year') => {
    const { id, image, review, priceInfo } = product;
    const { discountIncluded, total, initial } = priceInfo;
    const priceLabel = `per ${priceMode === 'year' ? 'jaar' : 'maand'}`;

    return {
      id,
      image,
      review,
      initialValue: initial,
      discount: discountIncluded,
      total,
      priceLabel,
    };
  };

  const renderComparableProducts = (): any => {
    return comparableProducts.map((comparableProduct) => {
      console.log({ comparableProduct });

      const { id, image, initialValue, discount, priceLabel, review, total } =
        props.isIProducts
          ? getIProductProps(comparableProduct, props.priceMode)
          : getProductProps(comparableProduct, props.priceMode);
      return (
        <Div
          bg="white"
          key={id + '_comparable'}
          display="flex"
          justifyContent="space-between"
          variant="compareProduct"
          minWidth={220}
          maxWidth={311}
          height={114}
        >
          <Div
            display="flex"
            flexDirection="column"
            height={119}
            css={
              isCar
                ? {
                    justifyContent: 'center',
                  }
                : {
                    flexGrow: 1,
                    flexShrink: 0,
                    flexBasis: 0,
                  }
            }
          >
            {renderImage(image)}
            {!isCar && renderReview(review)}
          </Div>
          <Div
            justifyContent="center"
            alignItems="end"
            display="flex"
            flexDirection="column"
            height={119}
            flexGrow={1}
            flexShrink={1}
            flexBasis={0}
          >
            <Price
              discount={discount}
              initialValue={initialValue}
              label={priceLabel}
              value={total}
              variant="priceCompare"
            />
          </Div>
          <Div pt={15} pr={10} pl={20}>
            <Icon
              data-testid="icon-close-compare"
              display="flex"
              fill="second.0"
              height={16}
              onClick={() => {
                onCompareClose(id);
              }}
              name="cancel"
              width={16}
              style={{
                cursor: 'pointer',
              }}
            />
          </Div>
        </Div>
      );
    });
  };

  const onMediaChange = (matches: boolean) => {
    if (matches && comparisonProducts.length > 2) {
      const id = comparisonProducts[comparableProducts.length - 1].id;
      onCompareClose(id);
    }
  };

  const resetProducts = () => {
    const tempVisibleProducts = props.products.map((product) => {
      product.compare = false;
      return product;
    });
    comparisonProductsFiled?.set({ value: '' });
    props.setProducts(tempVisibleProducts);
  };

  return (
    <Div
      display={shouldShowCompare ? 'flex' : 'none'}
      justifyContent="center"
      variant="modalCompareContainer"
      zIndex={2}
    >
      <Div
        display="flex"
        variant="modalCompare"
        height={['84px', 'auto', 'auto']}
      >
        <Div position="absolute" top={4} right={4}>
          <Icon
            data-testid="icon-close-compare"
            display="flex"
            fill="second.0"
            onClick={() => resetProducts()}
            size={24}
            name="cancel"
            style={{
              cursor: 'pointer',
            }}
          />
        </Div>
        <Div
          alignItems="center"
          justifyContent={['center', null, 'start']}
          variant="productWrapperCompare"
        >
          <Desktop>
            {renderComparableProducts()}
            {comparableProducts.length < 2 ? (
              <Div
                display={['none', 'none', 'flex']}
                minWidth={220}
                maxWidth={311}
                height={114}
                variant="compareEmptyProduct"
              ></Div>
            ) : undefined}
            {comparableProducts.length < 3 ? (
              <Div
                display={['none', 'none', 'flex']}
                minWidth={220}
                maxWidth={311}
                height={114}
                variant="compareEmptyProduct"
              ></Div>
            ) : undefined}
          </Desktop>
          <Div
            display="flex"
            flexGrow={0}
            flexShrink={0}
            flexBasis={0}
            mt={[0, 0, 20]}
          >
            <Desktop>
              <Button
                className="buildButton"
                variant="primary"
                onClick={() => onCompare()}
                data-testid="desktop-compare"
                disabled={comparableProducts.length < 2 ? true : false}
              >
                Vergelijk
              </Button>
            </Desktop>
            <Mobile onChange={onMediaChange}>
              <Div
                data-testid="media-change"
                onClick={() => onMediaChange(true)}
              />
              <Button
                className="buildButton"
                variant="primary"
                onClick={() => onCompare()}
                data-testid="mobile-compare"
                height={54}
                disabled={comparableProducts.length < 2 ? true : false}
              >
                Vergelijk {comparableProducts.length}/2 geslecteerd
              </Button>
            </Mobile>
          </Div>
        </Div>
      </Div>
    </Div>
  );
};
